import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import { useDebounce } from '@uidotdev/usehooks';
import { AxiosError, type AxiosResponse } from 'axios';
import _ from 'lodash';

import { type CalculateEndPeriod, calculateEndPeriod } from '../../api/utils';
import { type TypeModel, type TypeSharedModel } from '../../common/dataTypes';
import { ErrorContent } from '../../components/dataset/IFSTableSection';
import { type ModelType, type OrganizationRole } from '../../types';
import { type DefaultMutationError, useGetProjectById } from '../index';
import useModelComparisonStore from '../zustand/useModelComparisonStore';
import useModelStore from '../zustand/useModelStore';

import {
  useGetActualDataSchemaFiles,
  useImportActualData,
} from './useActualDataHook';
import {
  useGetReceivedUserDefinedExpenses,
  useGetSharedUserDefinedExpenses,
  useGetUserDefinedExpenses,
} from './useExpenseHook';
import { useGetIFS, useImportIFS } from './useIFSHook';
import {
  useGetModelById,
  useGetModelComparisonIsRunning,
  useGetModelComparisonOutput,
  useGetModelIsRunning,
  useGetModelOutput,
  useGetModelOutputWithActualData,
  useRunModelComparison,
  useRunModelOutput,
} from './useModelsHook';
import { useGetOrganizations } from './useOrganizationHook';
import {
  useGetReceivedModelById,
  useGetReceivedModelOutput,
} from './useReceivedModelsHooks';
import {
  useGetSharedModelById,
  useGetSharedModelOutput,
} from './useSharedModelsHook';

const calculateEndPeriodFn = async (input: CalculateEndPeriod) => {
  const { data }: AxiosResponse<any> = await calculateEndPeriod(input);

  return data;
};

export const useCalculateEndPeriod = (
  opts?: Partial<
    UseMutationOptions<
      Awaited<ReturnType<typeof calculateEndPeriodFn>>,
      DefaultMutationError,
      any,
      any
    >
  >
) =>
  useMutation({
    mutationKey: ['calculateEndPeriod'],
    mutationFn: (input: CalculateEndPeriod) => calculateEndPeriodFn(input),
    ...opts,
  });

type UseImportIFSProps = {
  inputFileRef: React.MutableRefObject<HTMLInputElement | null>;
  handleError?: () => void;
};

export const useHandleImportIFS = ({
  inputFileRef,
  handleError,
}: UseImportIFSProps) => {
  const [error, setError] = useState<{
    content: React.ReactNode;
  } | null>(null);

  const { projectId, orgId, datasetId } = useGetRouteParams();

  const { mutate: importIFS, isPending } = useImportIFS({
    onSuccess: () => {},
    onError: (error) => {
      if (error instanceof AxiosError) {
        setError({
          content: ErrorContent(error.response.data as string[]),
        });
      }
      handleError?.();
    },
  });

  const { data: ifs } = useGetIFS(
    {
      projectId,
      datasetId,
      orgId,
    },
    {
      enabled: !!datasetId && !!orgId && !!projectId,
    }
  );

  const importIfs = async (e: { target: { value: any; files: any } }) => {
    if (!projectId || !ifs) {
      return;
    }

    const formData = new FormData();

    Array.from(e.target.files as File[]).forEach((file: File) => {
      formData.append('ifs', file);
    });

    importIFS({
      datasetId,
      projectId,
      orgId,
      data: formData,
    });

    e.target.value = '';

    if (inputFileRef?.current?.value) {
      inputFileRef.current = null;
    }
  };

  return {
    importIfs,
    isPending,
    error,
  };
};

export const useHandleImportActualData = ({
  inputFileRef,
  handleError,
}: UseImportIFSProps) => {
  const [error, setError] = useState<{
    content: React.ReactNode;
  } | null>(null);

  const { projectId, orgId } = useGetRouteParams();

  const { mutate, isPending } = useImportActualData({
    onError: (error) => {
      if (error instanceof AxiosError) {
        setError({
          content: ErrorContent(error.response.data as string[] | string),
        });
      }
      handleError?.();
    },
  });

  const { data: ifs } = useGetActualDataSchemaFiles({
    projectId,
    orgId,
  });

  const importActualData = async (e: {
    target: { value: any; files: any };
  }) => {
    if (!projectId || !ifs) {
      return;
    }

    const formData = new FormData();

    Array.from(e.target.files as File[]).forEach((file: File) => {
      formData.append('files', file);
    });

    mutate({
      projectId,
      orgId,
      data: formData,
    });

    e.target.value = '';

    if (inputFileRef?.current?.value) {
      inputFileRef.current = null;
    }
  };

  return {
    importActualData,
    isPending,
    error,
  };
};

export const useGetRouteParams = () => {
  const [searchParams] = useSearchParams();

  const {
    org_id,
    project_id,
    dataset_id,
    expense_id,
    model_id,
    request_id,
    user_id,
    input_table_name,
    scenario_id,
    token,
    invitation_id,
  } = useParams();

  const orgId = Number(org_id || (searchParams.get('org_id') ?? undefined));

  const projectId = Number(
    project_id || (searchParams.get('project_id') ?? undefined)
  );

  const datasetId = Number(
    dataset_id || (searchParams.get('dataset_id') ?? undefined)
  );

  const expenseId = Number(
    expense_id || (searchParams.get('expense_id') ?? undefined)
  );

  const modelId = Number(
    model_id || (searchParams.get('model_id') ?? undefined)
  );

  const requestId = Number(
    request_id || (searchParams.get('request_id') ?? undefined)
  );

  const userId = Number(user_id || (searchParams.get('user_id') ?? undefined));

  const scenarioId = Number(
    scenario_id || (searchParams.get('scenario_id') ?? undefined)
  );

  const invitationId = Number(
    invitation_id || searchParams.get('invitation_id')
  );

  const inputTableName =
    input_table_name || searchParams.get('input_table_name');

  return {
    datasetId,
    expenseId,
    modelId,
    orgId,
    projectId,
    requestId,
    userId,
    invitationId,
    inputTableName,
    scenarioId,
    token: token || searchParams.get('token'),
  };
};

export const useGetRoles = (props?: {
  iProjectId?: number;
  iOrgId?: number;
}) => {
  const { orgId: rOrgId, projectId: rProjectId } = useGetRouteParams();

  const orgId = props?.iOrgId ?? rOrgId;
  const projectId = props?.iProjectId ?? rProjectId;

  const { data: organizations } = useGetOrganizations();

  const { data: project } = useGetProjectById(
    {
      projectId,
      orgId,
    },
    {
      enabled: !!projectId && !!orgId,
    }
  );

  return {
    projectRole: project?.role,
    orgRole: organizations?.find((org) => org.id === orgId)?.role,
  };
};

export const useCustomNavigate = () => {
  const navigate = useNavigate();

  return (path: string, params?: Record<string, string | number>) => {
    let formattedPath = path;

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        formattedPath = formattedPath.replace(`:${key}`, value.toString());
      });
    }

    navigate(formattedPath);
  };
};

export const isOrgAdmin = (role: OrganizationRole) =>
  role === 'organization_admin' || role === 'organization_owner';

export const isOrgSuperAdmin = (role: OrganizationRole) =>
  role === 'organization_owner';

export const isOrgMember = (role: OrganizationRole) =>
  role === 'organization_member';

export const isOrgGuest = (role: OrganizationRole) =>
  role === 'guest_organization_member';

export const useGetSearchResults = <T>({
  searchValue,
  data,
  filterFields = ['name', 'description'],
}: {
  searchValue: string;
  data: T[] | undefined;
  filterFields?: string[];
}) => {
  const [searchResults, setSearchResults] = useState<T[] | undefined>();

  const debouncedSearchTerm = useDebounce(searchValue, 300);

  useEffect(() => {
    if (!searchValue) {
      setSearchResults(data);
      return;
    }

    if (!!debouncedSearchTerm?.length && data) {
      const filteredDatasets = data.filter((i) =>
        filterFields.some((field) =>
          _.includes(
            field
              .split('.')
              .reduce((acc: any, curr) => acc[curr], i)
              ?.trim()
              .toLowerCase(),
            debouncedSearchTerm.trim().toLowerCase()
          )
        )
      );

      setSearchResults(filteredDatasets);
      return;
    }

    setSearchResults(undefined);
  }, [debouncedSearchTerm, data]);

  return searchResults;
};

export const useGetDistinguishModel = (data?: {
  modelId?: number;
  isActual?: boolean;
}) => {
  const [output, setOutput] = useState<
    Record<string, Record<string, number[] | string[]>> | undefined
  >(undefined);

  const [isOutputError, setIsOutputError] = useState<boolean | undefined>(
    undefined
  );

  const { modelId: routeModelId, orgId, projectId } = useGetRouteParams();

  const [searchParams] = useSearchParams();

  const type = searchParams.get('type') as ModelType;

  const isModelRunningClient = useModelStore(
    (state) => state.isRunningModelOutput
  );

  const isShared = type === 'shared';
  const isReceived = type === 'received';
  const isDefault = (!isShared && !isReceived) || !type;

  const modelId = data?.modelId ?? routeModelId;

  const { data: isModelRunningServer, refetch: refetchModelIsRunning } =
    useGetModelIsRunning(
      {
        modelId,
        orgId,
        projectId,
      },
      {
        enabled:
          !!modelId && !!orgId && !!projectId && isDefault && _.isEmpty(output),
        refetchIntervalInBackground: true,
        refetchInterval:
          isModelRunningClient === true && _.isEmpty(output) && 5000,
      }
    );

  const { mutateAsync: runModelOutput, isPending: isRunningModelOutput } =
    useRunModelOutput({
      onSuccess: async () => {
        await refetchModelIsRunning();
        await refetchDefaultModelOutput();

        if (data?.isActual) {
          await refetchDefaultModelOutputWithActual();
        }
      },
      onError: () => {
        refetchModelIsRunning();
      },
    });

  const {
    data: defaultModel,
    isLoading: isFetchingDefaultModel,
    isRefetching: isRefetchingDefaultModel,
    isFetchedAfterMount: isFetchedDefaultModel,
  } = useGetModelById(
    {
      modelId,
      orgId,
      projectId,
    },
    {
      enabled: !!modelId && !!orgId && !!projectId && isDefault,
    }
  );

  const {
    data: sharedModel,
    isLoading: isFetchingSharedModel,
    isRefetching: isRefetchingSharedModels,
    isFetchedAfterMount: isFetchedSharingModel,
  } = useGetSharedModelById(
    {
      modelId,
      orgId,
      projectId,
    },
    {
      enabled: !!modelId && !!orgId && !!projectId && isShared,
    }
  );

  const {
    data: receivedModel,
    isLoading: isFetchingReceivedModel,
    isRefetching: isRefetchingReceivedModel,
    isFetchedAfterMount: isFetchedReceivedModel,
  } = useGetReceivedModelById(
    {
      modelId,
      orgId,
      projectId,
    },
    {
      enabled: !!modelId && !!orgId && !!projectId && isReceived,
    }
  );

  const {
    data: defaultModelOutput,
    isLoading: isFetchingDefaultModelOutput,
    refetch: refetchDefaultModelOutput,
    isRefetching: isRefetchingDefaultModelOutput,
    isFetchedAfterMount: isFetchedDefaultModelOutput,
    isError: isDefaultModelOutputError,
  } = useGetModelOutput(
    {
      modelId,
      orgId,
      projectId,
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled:
        !!modelId &&
        !!orgId &&
        !!projectId &&
        isDefault &&
        isModelRunningServer === false &&
        !data?.isActual &&
        !isOutputError,
      throwOnError(e) {
        if (!isOutputError) {
          setIsOutputError(true);
        }
        return false;
      },
    }
  );

  const {
    data: defaultModelOutputWithActual,
    isLoading: isFetchingDefaultModelOutputWithActual,
    isRefetching: isRefetchingDefaultModelOutputWithActual,
    isFetchedAfterMount: isFetchedDefaultModelOutputWithActual,
    refetch: refetchDefaultModelOutputWithActual,
    isError: isDefaultModelOutputErrorWithActual,
  } = useGetModelOutputWithActualData(
    {
      modelId,
      orgId,
      projectId,
    },
    {
      enabled:
        !!modelId &&
        !!orgId &&
        !!projectId &&
        isDefault &&
        isModelRunningServer === false &&
        !isOutputError &&
        !!data?.isActual,
    }
  );

  const {
    data: sharedModelOutput,
    isLoading: isFetchingSharedModelOutput,
    isRefetching: isRefetchingSharedModelOutput,
    isFetched: isFetchedSharedModelOutput,
  } = useGetSharedModelOutput(
    {
      modelId,
      orgId,
      projectId,
    },
    {
      enabled:
        !!modelId && !!orgId && !!projectId && isShared && !isOutputError,
      throwOnError() {
        if (!isOutputError) {
          setIsOutputError(true);
        }
        return false;
      },
    }
  );

  const {
    data: receivedModelOutput,
    isLoading: isFetchingReceivedModelOutput,
    isRefetching: isRefetchingReceivedModelOutput,
    isFetched: isFetchedReceivedModelOutput,
  } = useGetReceivedModelOutput(
    {
      modelId,
      orgId,
      projectId,
    },
    {
      enabled:
        !!modelId && !!orgId && !!projectId && isReceived && !isOutputError,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );

  let model = defaultModel as TypeModel;

  let modelOutput = useMemo(
    () => (data?.isActual ? defaultModelOutputWithActual : defaultModelOutput),
    [data?.isActual, defaultModelOutputWithActual, defaultModelOutput]
  );

  if (isShared) {
    model = sharedModel as TypeSharedModel;
    modelOutput = sharedModelOutput;
  } else if (isReceived) {
    model = receivedModel as TypeSharedModel;
    modelOutput = receivedModelOutput;
  }

  useEffect(() => {
    if (modelOutput) {
      setIsOutputError(false);
      setOutput(modelOutput);
    }
  }, [modelOutput]);

  useEffect(() => {
    if (!isDefaultModelOutputError) {
      setIsOutputError(false);
      return;
    }

    setIsOutputError(isDefaultModelOutputError);
  }, [isDefaultModelOutputError]);

  const isRawLoading =
    isFetchingDefaultModel ||
    isFetchingSharedModel ||
    isFetchingDefaultModelOutput ||
    isFetchingReceivedModel ||
    isFetchingReceivedModelOutput ||
    isFetchingSharedModelOutput ||
    isRefetchingDefaultModel ||
    isRefetchingReceivedModel ||
    isRefetchingSharedModels ||
    isRefetchingDefaultModelOutput ||
    isRefetchingReceivedModelOutput ||
    isRefetchingSharedModelOutput ||
    isRunningModelOutput ||
    !!isModelRunningClient ||
    !!isModelRunningServer ||
    isFetchingDefaultModelOutputWithActual ||
    isRefetchingDefaultModelOutputWithActual;

  return {
    model,
    isShared,
    isDefault,
    isReceived,
    modelOutput,
    type,
    runModelOutput,
    refetchModelOutput: () => {
      refetchDefaultModelOutput();

      if (data?.isActual) {
        refetchDefaultModelOutputWithActual();
      }
    },
    isLoading: useDebounce(isRawLoading, 300),
    isFetchedModel:
      isFetchedDefaultModel || isFetchedReceivedModel || isFetchedSharingModel,
    isFetchedModelOutput:
      isFetchedDefaultModelOutput ||
      isFetchedSharedModelOutput ||
      isFetchedReceivedModelOutput ||
      isFetchedDefaultModelOutputWithActual,
    isFetchedModelOutputError: isOutputError,
    isRunningModelOutputClient: isModelRunningClient,
    isRunningModelOutputServer: isModelRunningServer || isRunningModelOutput,
  };
};

export const useGetModelComparison = ({
  modelA,
  modelB,
}: {
  modelA: string;
  modelB: string;
}) => {
  const { orgId, projectId } = useGetRouteParams();

  const {
    data: modelComparisonOutput,
    isError: isErrorComparisonOutput,
    isLoading: isLoadingComparisonOutput,
    isRefetching: isRefetchingComparisonOutput,
    refetch: refetchComparisonOutput,
  } = useGetModelComparisonOutput(
    {
      orgId,
      projectId,
      modelA,
      modelB,
    },
    {
      enabled: false,
      retryOnMount: false,
      refetchIntervalInBackground: false,
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  );

  const isRunningModelComparisonClient = useModelComparisonStore(
    (state) => state.isRunningModelComparisonOutput
  );

  const updateIsRunningModelComparison = useModelComparisonStore(
    (state) => state.updateIsRunningModelComparisonOutput
  );

  const {
    data: isRunningModelComparisonServer,
    refetch: refetchRunningModelComparisonServer,
  } = useGetModelComparisonIsRunning(
    {
      orgId,
      projectId,
      modelA,
      modelB,
    },
    {
      enabled:
        !!orgId &&
        !!modelA &&
        !!modelB &&
        !!projectId &&
        !isErrorComparisonOutput &&
        isRunningModelComparisonClient === true,
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchInterval: isRunningModelComparisonClient === true && 5000,
    }
  );

  const {
    mutateAsync: runModelComparison,
    isPending: isRunningModelComparison,
    error: runModelComparisonError,
  } = useRunModelComparison({
    onSuccess: async () => {
      await Promise.all([
        refetchRunningModelComparisonServer(),
        refetchComparisonOutput(),
      ]);
    },
    onError: () => {
      updateIsRunningModelComparison(false);
      refetchRunningModelComparisonServer();
    },
  });

  useEffect(() => {
    if (
      isRunningModelComparisonServer === false &&
      isRunningModelComparisonClient === true &&
      _.isEmpty(modelComparisonOutput)
    ) {
      refetchComparisonOutput();
    }
  }, [
    isRunningModelComparisonServer,
    isRunningModelComparisonClient,
    modelComparisonOutput,
  ]);

  useEffect(() => {
    if (
      isRunningModelComparisonServer === false &&
      !_.isEmpty(modelComparisonOutput)
    ) {
      updateIsRunningModelComparison(false);
    }
  }, [modelComparisonOutput, isRunningModelComparisonServer]);

  return {
    data: modelComparisonOutput,
    run: runModelComparison,
    error: runModelComparisonError,
    isError: isErrorComparisonOutput,
    refetch: refetchComparisonOutput,
    isLoading:
      isLoadingComparisonOutput ||
      isRefetchingComparisonOutput ||
      isRunningModelComparison,
    isRunningServer:
      !!isRunningModelComparisonServer || isRunningModelComparison,
    isRunningClient: isRunningModelComparisonClient,
  };
};

export const useGetDistinguishExpenseSets = (props?: {
  isRefetch?: 'always' | false | true;
}) => {
  const { orgId, projectId, modelId } = useGetRouteParams();

  const { isShared, isDefault, isReceived } = useGetDistinguishModel();

  const refetch = props?.isRefetch ?? false;

  const { data: defaultUserDefinedExpenses, isLoading: isLoadingDefault } =
    useGetUserDefinedExpenses(
      {
        orgId,
        projectId,
      },
      {
        enabled: !!orgId && !!projectId && isDefault,
        refetchOnWindowFocus: refetch,
      }
    );

  const { data: sharedUserDefinedExpenses, isLoading: isLoadingShared } =
    useGetSharedUserDefinedExpenses(
      {
        orgId,
        projectId,
        modelId,
      },
      {
        enabled: !!orgId && !!projectId && isShared,
        refetchOnWindowFocus: refetch,
      }
    );

  const { data: receivedUserDefinedExpenses, isLoading: isLoadingReceived } =
    useGetReceivedUserDefinedExpenses(
      {
        orgId,
        projectId,
        modelId,
      },
      {
        enabled: !!orgId && !!projectId && isReceived,
        refetchOnWindowFocus: refetch,
      }
    );

  const userDefinedExpenseSets = useMemo(
    () =>
      isDefault
        ? defaultUserDefinedExpenses
        : isShared
          ? sharedUserDefinedExpenses
          : isReceived
            ? receivedUserDefinedExpenses
            : [],
    [
      isDefault,
      isShared,
      isReceived,
      defaultUserDefinedExpenses,
      sharedUserDefinedExpenses,
      receivedUserDefinedExpenses,
    ]
  );

  return {
    userDefinedExpenseSets,
    isLoading: isLoadingDefault || isLoadingShared || isLoadingReceived,
  };
};
